import React from 'react';
import { Package, Calendar, CheckCircle, Building, Mail } from 'lucide-react';
import { clsx } from 'clsx';

interface QuotationPackage {
  strategy: string;
  description: string;
  items: Array<{
    name: string;
    price?: string;
    normalized_price?: number;
    description?: string;
    category?: string;
    brand?: string;
  }>;
  total_cost: number;
  cost_per_item: number;
  item_count: number;
}

interface QuotationResultsProps {
  quotationId: string;
  quotationData: any;
  packages: QuotationPackage[];
  validUntil: string;
  generatedAt: string;
  onPackageSelect?: (packageIndex: number) => void;
  selectedPackage?: number;
}

export const QuotationResults: React.FC<QuotationResultsProps> = ({
  quotationId,
  quotationData,
  packages,
  validUntil,
  generatedAt,
  onPackageSelect,
  selectedPackage
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-SG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Sales Quotation</h1>
            <p className="text-blue-100">Quotation ID: {quotationId}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-blue-100">Generated on</div>
            <div className="font-semibold">{formatDate(generatedAt)}</div>
            <div className="text-sm text-blue-100 mt-1">Valid until {validUntil}</div>
          </div>
        </div>
      </div>

      {/* Customer Information */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Building size={20} className="mr-2 text-blue-600" />
          Customer Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <Building size={16} className="text-gray-500" />
            <div>
              <div className="text-sm text-gray-500">Company</div>
              <div className="font-medium">{quotationData.company_name}</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Mail size={16} className="text-gray-500" />
            <div>
              <div className="text-sm text-gray-500">Email</div>
              <div className="font-medium">{quotationData.customer_email}</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Calendar size={16} className="text-gray-500" />
            <div>
              <div className="text-sm text-gray-500">Occasion</div>
              <div className="font-medium">{quotationData.occasion}</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Package size={16} className="text-gray-500" />
            <div>
              <div className="text-sm text-gray-500">Quantity</div>
              <div className="font-medium">{quotationData.number_of_packs} packs</div>
            </div>
          </div>
        </div>
        {quotationData.special_requests && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Special Requirements</div>
            <div className="text-sm text-gray-700">{quotationData.special_requests}</div>
          </div>
        )}
      </div>

      {/* Package Options */}
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Package size={20} className="mr-2 text-blue-600" />
          Package Options
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {packages.map((pkg, index) => (
            <div
              key={index}
              className={clsx(
                'border rounded-lg p-4 cursor-pointer transition-all duration-200',
                selectedPackage === index
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              )}
              onClick={() => onPackageSelect?.(index)}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">{pkg.strategy}</h3>
                {selectedPackage === index && (
                  <CheckCircle size={20} className="text-blue-600" />
                )}
              </div>
              
              <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Items</span>
                  <span className="text-sm font-medium">{pkg.item_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Cost per item</span>
                  <span className="text-sm font-medium">{formatCurrency(pkg.cost_per_item)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium text-gray-900">Total Cost</span>
                  <span className="text-lg font-bold text-blue-600">{formatCurrency(pkg.total_cost)}</span>
                </div>
              </div>
              
              {/* Items Preview */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Items included:</div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {pkg.items.slice(0, 3).map((item, itemIndex) => (
                    <div key={itemIndex} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      <div className="font-medium">{item.name}</div>
                      {item.normalized_price && (
                        <div className="text-green-600">{formatCurrency(item.normalized_price)}</div>
                      )}
                    </div>
                  ))}
                  {pkg.items.length > 3 && (
                    <div className="text-xs text-gray-500 text-center py-1">
                      +{pkg.items.length - 3} more items
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 p-6 border-t border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">PrintnGift</h3>
            <p className="text-sm text-gray-600">Your Corporate Gift Solutions Partner</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Questions about this quotation?</div>
            <div className="text-sm text-gray-600">Contact our sales team for assistance</div>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            This quotation is valid until {validUntil}. Terms and conditions apply.
          </p>
        </div>
      </div>
    </div>
  );
};